import { supabase } from "@/integrations/supabase/client";

export type AuditCategory =
	| "authentication"
	| "user_management"
	| "attendance"
	| "sessions"
	| "venues"
	| "incidents"
	| "coach_documents"
	| "reminders"
	| "data_sync"
	| "settings"
	| "timesheets";

export interface AuditLogParams {
	action: string;
	category: AuditCategory;
	entityType?: string;
	entityId?: string;
	oldValue?: unknown;
	newValue?: unknown;
	metadata?: unknown;
}

// ============= Audit Log Retry Queue =============
// SEC-008 Fix: Implement retry mechanism for failed audit logs

interface QueuedAuditLog extends AuditLogParams {
	timestamp: number;
	retryCount: number;
	userId?: string;
	userAgent: string;
}

const STORAGE_KEY = "audit_log_queue";
const MAX_QUEUE_SIZE = 100;
const MAX_RETRY_COUNT = 3;
const RETRY_DELAY_MS = 5000; // 5 seconds base delay
const FAILURE_THRESHOLD = 5; // Alert after this many consecutive failures

let consecutiveFailures = 0;
let isRetrying = false;
let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;

/**
 * Load queued audit logs from localStorage
 */
function loadQueue(): QueuedAuditLog[] {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		return stored ? JSON.parse(stored) : [];
	} catch {
		return [];
	}
}

/**
 * Save queued audit logs to localStorage
 */
function saveQueue(queue: QueuedAuditLog[]): void {
	try {
		// Limit queue size to prevent localStorage from growing too large
		const trimmedQueue = queue.slice(-MAX_QUEUE_SIZE);
		localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedQueue));
	} catch (error) {
		console.error("[AuditLog] Failed to save queue to localStorage:", error);
	}
}

/**
 * Add a failed audit log to the retry queue
 */
function enqueueFailedLog(params: AuditLogParams, userId?: string): void {
	const queue = loadQueue();

	const queuedLog: QueuedAuditLog = {
		...params,
		timestamp: Date.now(),
		retryCount: 0,
		userId,
		userAgent:
			typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
	};

	queue.push(queuedLog);
	saveQueue(queue);

	// Schedule retry if not already scheduled
	scheduleRetry();
}

/**
 * Schedule a retry attempt with exponential backoff
 */
function scheduleRetry(): void {
	if (retryTimeoutId || isRetrying) return;

	const queue = loadQueue();
	if (queue.length === 0) return;

	// Calculate delay with exponential backoff based on consecutive failures
	const delay = RETRY_DELAY_MS * Math.pow(2, Math.min(consecutiveFailures, 5));

	retryTimeoutId = setTimeout(() => {
		retryTimeoutId = null;
		retryFailedAudits();
	}, delay);
}

/**
 * Attempt to retry failed audit logs
 */
async function retryFailedAudits(): Promise<void> {
	if (isRetrying) return;

	const queue = loadQueue();
	if (queue.length === 0) return;

	isRetrying = true;

	const remainingQueue: QueuedAuditLog[] = [];
	let successCount = 0;
	let failCount = 0;

	for (const log of queue) {
		// Skip if max retries exceeded
		if (log.retryCount >= MAX_RETRY_COUNT) {
			console.warn(
				"[AuditLog] Max retries exceeded, dropping log:",
				log.action
			);
			continue;
		}

		try {
			const { error } = await supabase.from("audit_logs").insert({
				user_id: log.userId,
				action: log.action,
				category: log.category,
				entity_type: log.entityType,
				entity_id: log.entityId,
				old_value: log.oldValue
					? JSON.parse(JSON.stringify(log.oldValue))
					: null,
				new_value: log.newValue
					? JSON.parse(JSON.stringify(log.newValue))
					: null,
				metadata: {
					...((log.metadata as Record<string, unknown>) || {}),
					_retried: true,
					_originalTimestamp: log.timestamp,
					_retryCount: log.retryCount + 1,
				},
				user_agent: log.userAgent,
			});

			if (error) {
				throw error;
			}

			successCount++;
		} catch {
			// Re-queue with incremented retry count
			remainingQueue.push({
				...log,
				retryCount: log.retryCount + 1,
			});
			failCount++;
		}
	}

	saveQueue(remainingQueue);
	isRetrying = false;

	if (successCount > 0) {
		console.info(
			`[AuditLog] Retry: ${successCount} succeeded, ${failCount} failed`
		);
		consecutiveFailures = 0;
	}

	// Schedule another retry if there are still items in the queue
	if (remainingQueue.length > 0) {
		scheduleRetry();
	}
}

/**
 * Report audit log failure (can be extended to send to error tracking service)
 */
function reportAuditFailure(error: unknown, params: AuditLogParams): void {
	consecutiveFailures++;

	// Log with structured data for monitoring
	console.error("[AuditLog] Failure:", {
		error: error instanceof Error ? error.message : String(error),
		action: params.action,
		category: params.category,
		consecutiveFailures,
		queueSize: loadQueue().length,
	});

	// Alert if threshold exceeded
	if (consecutiveFailures === FAILURE_THRESHOLD) {
		console.error(
			`[AuditLog] ALERT: ${FAILURE_THRESHOLD} consecutive audit log failures. ` +
				"Security events may not be recorded. Check Supabase connection and RLS policies."
		);

		// In production, you could:
		// - Send to error tracking service (Sentry, etc.)
		// - Trigger an alert via webhook
		// - Store in a separate error log
	}
}

/**
 * Get audit log queue status (for monitoring/debugging)
 */
export function getAuditLogStatus(): {
	queueSize: number;
	consecutiveFailures: number;
	isRetrying: boolean;
} {
	return {
		queueSize: loadQueue().length,
		consecutiveFailures,
		isRetrying,
	};
}

/**
 * Manually trigger a retry of failed audit logs
 */
export function triggerAuditRetry(): void {
	retryFailedAudits();
}

/**
 * Clear the audit log queue (use with caution - for testing/recovery)
 */
export function clearAuditQueue(): void {
	localStorage.removeItem(STORAGE_KEY);
	consecutiveFailures = 0;
}

// ============= Main Audit Log Function =============

export async function logAuditEvent({
	action,
	category,
	entityType,
	entityId,
	oldValue,
	newValue,
	metadata,
}: AuditLogParams): Promise<void> {
	const params: AuditLogParams = {
		action,
		category,
		entityType,
		entityId,
		oldValue,
		newValue,
		metadata,
	};

	try {
		const {
			data: { user },
		} = await supabase.auth.getUser();

		const { error } = await supabase.from("audit_logs").insert({
			user_id: user?.id,
			action,
			category,
			entity_type: entityType,
			entity_id: entityId,
			old_value: oldValue ? JSON.parse(JSON.stringify(oldValue)) : null,
			new_value: newValue ? JSON.parse(JSON.stringify(newValue)) : null,
			metadata: metadata ? JSON.parse(JSON.stringify(metadata)) : null,
			user_agent: navigator.userAgent,
		});

		if (error) {
			throw error;
		}

		// Reset failure counter on success
		consecutiveFailures = 0;

		// Try to process any queued items on successful connection
		if (loadQueue().length > 0 && !isRetrying) {
			retryFailedAudits();
		}
	} catch (error) {
		// Report the failure
		reportAuditFailure(error, params);

		// Queue for retry
		try {
			const {
				data: { user },
			} = await supabase.auth.getUser();
			enqueueFailedLog(params, user?.id);
		} catch {
			// Even getting user failed, queue without user ID
			enqueueFailedLog(params);
		}
	}
}

// Initialize: attempt to retry any queued items on module load
if (typeof window !== "undefined") {
	// Delay initial retry to avoid blocking app startup
	setTimeout(() => {
		const queue = loadQueue();
		if (queue.length > 0) {
			console.info(
				`[AuditLog] Found ${queue.length} queued audit logs, scheduling retry`
			);
			scheduleRetry();
		}
	}, 10000); // Wait 10 seconds after page load
}
